import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { ImagePlus, Film, Loader2, AlertTriangle } from "lucide-react";

const PHOTO_MAX = 10_000_000;
const VIDEO_MAX = 60_000_000;
const MAX_EDGE = 2000;
const UNSUPPORTED_VIDEO =
  "This video format isn't supported yet. Please export or share it as an MP4 and try again.";

async function shrinkPhoto(file) {
  if (file.size <= 3_500_000) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise(res => canvas.toBlob(res, "image/jpeg", 0.85));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], "photo.jpg", { type: "image/jpeg" });
  } catch { return file; }
}

/** Confirm the browser can actually decode this video, and grab a poster frame
 *  while we're at it. Rejects anything unplayable (e.g. MOV/HEVC) BEFORE we
 *  store a file that would only work for the uploader. */
function probeVideo(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    v.playsInline = true;
    const fail = () => { URL.revokeObjectURL(url); reject(new Error(UNSUPPORTED_VIDEO)); };
    const timer = setTimeout(fail, 15000);
    v.onerror = fail;
    v.onloadeddata = () => {
      clearTimeout(timer);
      const grab = () => {
        try {
          const scale = Math.min(1, 1280 / Math.max(v.videoWidth || 1, v.videoHeight || 1));
          const canvas = document.createElement("canvas");
          canvas.width = Math.round((v.videoWidth || 640) * scale);
          canvas.height = Math.round((v.videoHeight || 360) * scale);
          canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(blob => {
            URL.revokeObjectURL(url);
            resolve({ duration: v.duration || 0, poster: blob });
          }, "image/jpeg", 0.8);
        } catch {
          URL.revokeObjectURL(url);
          resolve({ duration: v.duration || 0, poster: null });
        }
      };
      if (v.currentTime < 0.05 && v.duration > 0.2) {
        v.onseeked = grab;
        v.currentTime = Math.min(0.5, v.duration / 3);
      } else grab();
    };
    v.src = url;
  });
}

const upload = async (path, file, onProgress) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(path, form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: e => onProgress?.(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
  });
  return data;
};

/** Photo + video picker for Profile Posts. Calls onAdd({type,url,poster_url,path}). */
export default function MediaUploadButton({ onAdd, disabled }) {
  const photoRef = useRef(null);
  const videoRef = useRef(null);
  const [busy, setBusy] = useState("");
  const [pct, setPct] = useState(0);
  const [error, setError] = useState("");

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (photoRef.current) photoRef.current.value = "";
    if (!files.length) return;
    setError(""); setBusy("photo"); setPct(0);
    try {
      for (const raw of files) {
        const file = await shrinkPhoto(raw);
        if (file.size > PHOTO_MAX) throw new Error("That photo is larger than 10MB even after resizing.");
        const data = await upload("/upload", file, setPct);
        onAdd({ type: "photo", url: `${process.env.REACT_APP_BACKEND_URL}${data.url}`, path: data.path });
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "That photo didn't upload.");
    } finally { setBusy(""); setPct(0); }
  };

  const handleVideo = async (e) => {
    const file = e.target.files?.[0];
    if (videoRef.current) videoRef.current.value = "";
    if (!file) return;
    setError(""); setPct(0);
    const name = (file.name || "").toLowerCase();
    const okType = /video\/(mp4|webm)/.test(file.type || "") || /\.(mp4|webm)$/.test(name);
    if (!okType) { setError(UNSUPPORTED_VIDEO); return; }
    if (file.size > VIDEO_MAX) { setError("That video is over the 60MB limit — please trim it or export a smaller file."); return; }
    setBusy("probe");
    try {
      const { poster } = await probeVideo(file);
      setBusy("video");
      const data = await upload("/upload/video", file, setPct);
      let poster_url = "";
      if (poster) {
        try {
          const p = await upload("/upload", new File([poster], "poster.jpg", { type: "image/jpeg" }));
          poster_url = `${process.env.REACT_APP_BACKEND_URL}${p.url}`;
        } catch { /* poster is a nicety, not a blocker */ }
      }
      onAdd({ type: "video", url: `${process.env.REACT_APP_BACKEND_URL}${data.url}`, poster_url, path: data.path });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || UNSUPPORTED_VIDEO);
    } finally { setBusy(""); setPct(0); }
  };

  const working = !!busy;
  return (
    <div>
      <input ref={photoRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotos} data-testid="profile-photo-input"/>
      <input ref={videoRef} type="file" accept="video/mp4,video/webm" className="hidden" onChange={handleVideo} data-testid="profile-video-input"/>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" disabled={disabled || working} onClick={() => photoRef.current?.click()}
          className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold rounded-full border border-[#D3DAD5] bg-white hover:border-[#2E6B57] disabled:opacity-50 transition-colors"
          data-testid="add-photos-btn">
          {busy === "photo" ? <Loader2 size={15} className="animate-spin"/> : <ImagePlus size={15}/>}
          {busy === "photo" ? `Uploading ${pct}%` : "Add photos"}
        </button>
        <button type="button" disabled={disabled || working} onClick={() => videoRef.current?.click()}
          className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold rounded-full border border-[#D3DAD5] bg-white hover:border-[#2E6B57] disabled:opacity-50 transition-colors"
          data-testid="add-video-btn">
          {busy === "probe" || busy === "video" ? <Loader2 size={15} className="animate-spin"/> : <Film size={15}/>}
          {busy === "probe" ? "Checking video…" : busy === "video" ? `Uploading ${pct}%` : "Add video"}
        </button>
        <span className="text-[11px] text-[#222321]/45">MP4 or WebM · up to 60MB</span>
      </div>
      {error && (
        <p className="mt-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-2.5 py-2 flex items-start gap-1.5 max-w-md" data-testid="media-upload-error">
          <AlertTriangle size={13} className="shrink-0 mt-0.5"/> <span>{error} Your caption is still here — nothing was posted.</span>
        </p>
      )}
    </div>
  );
}
